import { useTheme } from '@balance-web/theme';
import { useMemo } from 'react';

export type BadgeToneColors =
  | 'primary'
  | 'primary-inverse'
  | 'secondary'
  | 'secondary-inverse'
  | 'neutral'
  | 'neutral-inverse'
  | 'critical'
  | 'critical-inverse'
  | 'positive'
  | 'cautious'
  | 'informative'
  | 'disabled';

export type IndicatorToneColors = Exclude<
  BadgeToneColors,
  | 'primary-inverse'
  | 'secondary-inverse'
  | 'secondary'
  | 'neutral-inverse'
  | 'critical-inverse'
>;

export function useBadgeToneColor({
  tone = 'neutral',
}: {
  tone?: BadgeToneColors;
}) {
  const { semantic } = useTheme();

  const foregroundColor = useMemo(() => {
    if (tone === 'primary') {
      return semantic.colour.primary.foreground;
    }

    if (tone === 'primary-inverse') {
      return semantic.colour.primary.inverse.foreground;
    }

    if (tone === 'secondary') {
      return semantic.colour.text.textInverted;
    }

    if (tone === 'secondary-inverse') {
      return semantic.colour.text.reserved.critical;
    }

    if (tone === 'neutral') {
      return semantic.colour.neutral.inverse.foreground;
    }

    if (tone === 'neutral-inverse') {
      return semantic.colour.neutral.container.foreground;
    }

    if (tone === 'critical') {
      return semantic.colour.reserved.critical.foreground;
    }

    if (tone === 'critical-inverse') {
      return semantic.colour.reserved.critical.inverse.foreground;
    }

    if (tone === 'positive') {
      return semantic.colour.reserved.success.foreground;
    }

    if (tone === 'cautious') {
      return semantic.colour.reserved.warning.foreground;
    }

    if (tone === 'informative') {
      return semantic.colour.reserved.info.foreground;
    }

    if (tone === 'disabled') {
      return semantic.colour.text.textDisabled;
    }
  }, [semantic, tone]);

  const backgroundColor = useMemo(() => {
    if (tone === 'primary') {
      return semantic.colour.primary.background;
    }

    if (tone === 'primary-inverse') {
      return semantic.colour.primary.inverse.background;
    }

    if (tone === 'secondary') {
      return semantic.colour.reserved.critical.background;
    }

    if (tone === 'secondary-inverse') {
      return semantic.colour.reserved.critical.inverse.background;
    }

    if (tone === 'neutral') {
      return semantic.colour.neutral.inverse.background;
    }

    if (tone === 'neutral-inverse') {
      return semantic.colour.surface.neutral.background3;
    }

    if (tone === 'critical') {
      return semantic.colour.reserved.critical.background;
    }

    if (tone === 'critical-inverse') {
      return semantic.colour.reserved.critical.inverse.background;
    }

    if (tone === 'positive') {
      return semantic.colour.reserved.success.background;
    }

    if (tone === 'cautious') {
      return semantic.colour.reserved.warning.background;
    }

    if (tone === 'informative') {
      return semantic.colour.reserved.info.background;
    }

    if (tone === 'disabled') {
      return semantic.colour.neutral.inverse.interactions.hover;
    }
  }, [semantic, tone]);

  return { backgroundColor, foregroundColor };
}

export function useIndicatorToneColor({
  tone = 'neutral',
}: {
  tone?: IndicatorToneColors;
}) {
  const { semantic } = useTheme();

  const backgroundColor = useMemo(() => {
    if (tone === 'primary') {
      return semantic.colour.decorative.purple.surface;
    }

    if (tone === 'neutral') {
      return semantic.colour.neutral.container.foreground;
    }

    if (tone === 'critical') {
      return semantic.colour.reserved.critical.background;
    }

    if (tone === 'positive') {
      return semantic.colour.reserved.success.background;
    }

    if (tone === 'cautious') {
      return semantic.colour.reserved.warning.background;
    }

    if (tone === 'informative') {
      return semantic.colour.reserved.info.background;
    }

    if (tone === 'disabled') {
      return semantic.colour.text.textDisabled;
    }
  }, [semantic, tone]);

  return { backgroundColor };
}
